import { useEffect, useState } from 'react';
import PageHeader from '../components/common/PageHeader';
import ShellLink from '../components/common/ShellLink';
import { useShowInternals } from '../contexts/InternalsContext';
import { LineChart, Line, Tooltip } from 'recharts';
import { withReturnTo } from '../components/common/returnPath';
import api from '../services/api';
import { useEntitlements } from '../contexts/EntitlementContext';
import { useAuth } from '../contexts/AuthContext';
import type { DashboardStats, TrendingKeyword, ParserOpportunity } from '../types';

function getStatusColor(status: string): string {
  switch (status) {
    case 'COMPLETED': return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
    case 'IN_PROGRESS': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
    case 'WAITING_FOR_USER_ACTION': return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300';
    case 'WAITING_FOR_SYSTEM_ACTION': return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
    case 'FAILED':
    case 'ERROR': return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
    case 'READY_TO_DEPLOY': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
    case 'PENDING': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
    case 'QUEUED': return 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300';
    case 'DRAFT': return 'bg-slate-100 dark:bg-slate-900/40 text-slate-600 dark:text-slate-400';
    case 'CANCELLED': return 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    case 'SKIPPED': return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  }
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function Dashboard() {
  const { held } = useEntitlements();
  const { user } = useAuth();
  const showInternals = useShowInternals();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [growTab, setGrowTab] = useState<'grow' | 'optimize'>('grow');
  const [pendingApprovals, setPendingApprovals] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await api.getDashboardStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Admins land here and immediately see whether there's a non-empty queue of
  // signups awaiting their approval.
  useEffect(() => {
    if (!user?.is_admin) return;
    api.getPendingUserCount()
      .then(setPendingApprovals)
      .catch(() => {});
  }, [user?.is_admin]);

  // Un-parsed URL shapes we keep re-reading with an LLM. Surfaced here because the cost
  // is invisible where it's incurred (a crawl) and only actionable somewhere else
  // entirely (Site Parsers) — nobody goes looking for it.
  const [parserOpp, setParserOpp] = useState<ParserOpportunity | null>(null);
  // The artifact shelf, for "what needs you". Fetched separately and allowed to
  // fail quietly: a stalled artifacts query should not blank the whole of Home.
  const [artifactCounts, setArtifactCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    api
      .listArtifacts()
      .then((r) => setArtifactCounts(r.state_counts ?? {}))
      .catch(() => setArtifactCounts({}));
  }, []);
  useEffect(() => {
    if (!showInternals) return;
    api
      .getParserOpportunity()
      .then(setParserOpp)
      .catch(() => {});
  }, [showInternals]);

  if (loading) {
    return <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading dashboard...</div>;
  }

  if (error || !stats) {
    return <div className="text-center py-8 text-red-500">{error || 'Failed to load dashboard'}</div>;
  }

  // WHAT NEEDS YOU, not what exists. The four cards here used to be Projects /
  // Topics / Keywords / Pages, of which TWO had `link: null` — dead tiles that
  // counted something you could not then go and look at. Worse, they answered a
  // question nobody arrives with: the number of keywords on the account does not
  // tell you what to do next, and this screen is the first thing anyone sees.
  //
  // These are the states work gets STUCK in, and every one is clickable.
  const attention: { title: string; count: number; link: string; subtitle: string; color: string }[] = [
    {
      title: 'Drafts',
      count: artifactCounts.DRAFT ?? 0,
      link: '/artifacts?state=DRAFT',
      subtitle: 'Made, not finished',
      color: 'bg-gray-500',
    },
    {
      title: 'Ready to ship',
      count: artifactCounts.READY ?? 0,
      link: '/artifacts?state=READY',
      subtitle: 'Cleared, waiting on you',
      color: 'bg-amber-500',
    },
    {
      title: 'Building',
      count: artifactCounts.BUILDING ?? 0,
      link: '/artifacts?state=BUILDING',
      subtitle: 'Runs in flight',
      color: 'bg-blue-500',
    },
    {
      title: 'Failed',
      count: artifactCounts.FAILED ?? 0,
      link: '/artifacts?state=FAILED',
      subtitle: 'Runs that did not finish',
      color: 'bg-red-500',
    },
  ];
  const nothingWaiting = attention.every((c) => c.count === 0);

  // The starting points this user can actually reach, in the order they read.
  const starters = [
    { product: 'BOARDROOM' as const, lead: 'Open a', label: 'boardroom', to: '/boardroom' },
    { product: 'BEATS' as const, lead: 'Run a', label: 'news beat', to: '/news-beats' },
    { product: 'PUBLISH' as const, lead: 'Look at', label: 'what’s trending', to: '/trending' },
  ].filter((s) => held.includes(s.product));


  const coveragePct = stats.keyword_count > 0
    ? Math.round(((stats.keyword_count - stats.keywords_without_pages) / stats.keyword_count) * 100)
    : 0;
  const keywordsWithPages = stats.keyword_count - stats.keywords_without_pages;

  return (
    <div>
      <PageHeader title="Home" subtitle="What needs you today, and what you have in flight." />

      {/* Admin: applicants awaiting approval */}
      {pendingApprovals > 0 && (
        <ShellLink
          to="/settings?tab=users"
          className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-5 py-4 mb-6 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold">
              {pendingApprovals}
            </span>
            <span className="text-sm font-medium text-amber-900 dark:text-amber-200">
              {pendingApprovals === 1
                ? '1 user is awaiting your approval'
                : `${pendingApprovals} users are awaiting your approval`}
            </span>
          </div>
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Review →</span>
        </ShellLink>
      )}

      {/* WHAT NEEDS YOU. Every tile is a state work gets stuck in, and every one
          is clickable — the row this replaced counted Topics and Keywords with
          `link: null`, so the two biggest numbers on the landing screen went
          nowhere. */}
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
        What needs you
      </h2>
      {nothingWaiting ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">Nothing is waiting on you.</p>
          {/* SUGGEST ONLY WHAT THEY CAN OPEN. This line used to name all three
              products unconditionally, so the emptiest screen in the app — the
              one you see before you have made anything — was also where someone
              who bought News Beats got invited to start a brainstorm they have
              no access to. An empty state that advertises a locked door is
              worse than one that says nothing. */}
          {starters.length > 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {starters.map((s, i) => (
                <span key={s.to}>
                  {i > 0 && (i === starters.length - 1 ? ', or ' : ', ')}
                  {i === 0 ? s.lead : s.lead.toLowerCase()}{' '}
                  <ShellLink to={s.to} className="text-blue-600 dark:text-blue-400 hover:underline">
                    {s.label}
                  </ShellLink>
                </span>
              ))}
              .
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {attention.map((card) => (
            <ShellLink
              key={card.title}
              to={card.link}
              className={`bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow ${
                card.count === 0 ? 'opacity-50' : ''
              }`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${card.color} rounded-md p-3`}>
                    <span className="text-white text-xl font-bold">
                      {card.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {card.title}
                      </dt>
                      <dd className="text-sm text-gray-400 dark:text-gray-500">{card.subtitle}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </ShellLink>
          ))}
        </div>
      )}

      {/* The library, as one line rather than four tiles. These are facts about
          the account, not work to do, and giving them the same weight as the row
          above is what made this screen answer a question nobody asked. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
        <ShellLink to="/artifacts" className="hover:text-gray-800 dark:hover:text-gray-200">
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {Object.values(artifactCounts).reduce((a, b) => a + b, 0).toLocaleString()}
          </span>{' '}
          artifacts
        </ShellLink>
        <ShellLink to="/subjects" className="hover:text-gray-800 dark:hover:text-gray-200">
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {stats.project_count.toLocaleString()}
          </span>{' '}
          projects
        </ShellLink>
        <ShellLink to="/pages" className="hover:text-gray-800 dark:hover:text-gray-200">
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {stats.total_pages.toLocaleString()}
          </span>{' '}
          pages
        </ShellLink>
        <span>
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {stats.topic_count.toLocaleString()}
          </span>{' '}
          topics
        </span>
        <span>
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {stats.keyword_count.toLocaleString()}
          </span>{' '}
          keywords
        </span>
      </div>

      {/* Un-parsed URL shapes: an LLM re-reads these pages on every crawl. The cost is
          invisible where it's paid and fixable only on another screen, so nudge here. */}
      {showInternals && parserOpp && parserOpp.candidate_count > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-5 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                You're paying an LLM to re-read {parserOpp.pages_seen.toLocaleString()} pages
              </h2>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                {parserOpp.candidate_count.toLocaleString()} URL shapes keep coming back with
                no parser, so every crawl sends the whole page to a model again.
                {parserOpp.measured_extractions > 0 && (
                  <>
                    {' '}That's roughly{' '}
                    <b>{(parserOpp.estimated_tokens / 1_000_000).toFixed(1)}M tokens</b>{' '}
                    <span
                      className="underline decoration-dotted cursor-help"
                      title={`Estimate: ${parserOpp.avg_tokens_per_extraction.toLocaleString()} tokens per extraction (measured over ${parserOpp.measured_extractions.toLocaleString()} calls) x ${parserOpp.pages_seen.toLocaleString()} pages seen. Not a measured total.`}
                    >
                      (estimated)
                    </span>
                    . A parser extracts them for free, forever.
                  </>
                )}
              </p>
              {parserOpp.top.length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                  Most seen:{' '}
                  {parserOpp.top
                    .map((t) => `${t.host}${t.url_template} (${t.encounter_count}x)`)
                    .join(' · ')}
                </p>
              )}
            </div>
            <ShellLink
              to="/settings?tab=network&sub=parsers"
              className="px-3 py-2 text-sm rounded bg-amber-600 text-white hover:bg-amber-700 shrink-0"
            >
              Build parsers →
            </ShellLink>
          </div>
        </div>
      )}

      {/* The Content Pipeline block stood here: eight groups over
          PageBuildStatus — Not Started, Queued, Building, Needs Your Review,
          Ready to Deploy, Done, Errors, Stopped.

          It said the same thing as the cards above it in a different language,
          and only about pages. A page cleared to ship was "Ready to ship" in
          one block and "Ready to Deploy" in the other, on one screen; a
          briefing appeared in neither. That is the exact split the artifact
          vocabulary exists to close, sitting on the app's front door.

          The build-stage detail it offered is real and lives on /pages, which
          is the page workbench and one click from the cards above. */}

      {/* Row 2b: Pending Recommendations */}
      {stats.pending_rec_keyword_count > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Pending Recommendations</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.pending_rec_keyword_count.toLocaleString()}</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">keywords to review</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Across {stats.pending_rec_topic_count.toLocaleString()} topic{stats.pending_rec_topic_count !== 1 ? 's' : ''}
              </p>
            </div>
            <ShellLink
              to="/trending?recommendations_only=true"
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            >
              Review recommendations &rarr;
            </ShellLink>
          </div>
        </div>
      )}

      {/* Row 3: Top Growing Keywords + Keyword Coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top Growing Keywords — tabbed */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Top Growing Keywords</h2>
            <div className="flex gap-1">
              {(['grow', 'optimize'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setGrowTab(t)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    growTab === t
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t === 'grow' ? 'Grow' : 'Optimize'}
                </button>
              ))}
            </div>
          </div>

          {growTab === 'grow' ? (
            stats.top_growing_grow.length === 0 ? (
              <p className="text-gray-400 dark:text-gray-500 text-sm">No untracked growing keywords found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b">
                      <th className="pb-2 font-medium">Keyword</th>
                      <th className="pb-2 font-medium">Trend</th>
                      <th className="pb-2 font-medium text-right">Growth</th>
                      <th className="pb-2 font-medium text-right">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_growing_grow.map((kw: TrendingKeyword) => (
                      <tr key={kw.keyword_id} className="border-b border-gray-50 dark:border-gray-700">
                        <td className="py-2 pr-3">
                          <ShellLink
                            to={`/trending?keyword_search=${encodeURIComponent(kw.keyword_value)}`}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 truncate max-w-[200px] block"
                            title={kw.keyword_value}
                          >
                            {kw.keyword_value}
                          </ShellLink>
                        </td>
                        <td className="py-2 pr-3">
                          <LineChart width={80} height={24} data={kw.monthly_data}>
                            <Tooltip
                              offset={0}
                              position={{ y: -30 }}
                              content={({ active, payload }) => {
                                if (!active || !payload?.[0]) return null;
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">
                                    {d.month} {d.year}: {Number(d.searches).toLocaleString()}
                                  </div>
                                );
                              }}
                            />
                            <Line type="monotone" dataKey="searches" stroke={kw.growth_pct != null && kw.growth_pct > 0 ? '#16a34a' : '#dc2626'} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                          </LineChart>
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <span className={kw.growth_pct != null && kw.growth_pct > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {kw.growth_pct != null ? `${kw.growth_pct > 0 ? '+' : ''}${kw.growth_pct.toLocaleString(undefined, { maximumFractionDigits: 0 })}%` : '-'}
                          </span>
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-300">
                          {kw.avg_monthly_searches != null ? kw.avg_monthly_searches.toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            stats.top_growing_optimize.length === 0 ? (
              <p className="text-gray-400 dark:text-gray-500 text-sm">No stale pages to optimize - nice work!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b">
                      <th className="pb-2 font-medium">Keyword</th>
                      <th className="pb-2 font-medium">Trend</th>
                      <th className="pb-2 font-medium text-right">Growth</th>
                      <th className="pb-2 font-medium text-right">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_growing_optimize.map((kw: TrendingKeyword) => (
                      <tr key={kw.keyword_id} className="border-b border-gray-50 dark:border-gray-700">
                        <td className="py-2 pr-3">
                          <div>
                            <ShellLink
                              to={withReturnTo(
                                `/page-builder/${kw.managed_page_id}`,
                                '/',
                                'Home',
                              )}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 truncate max-w-[200px] block"
                              title={kw.keyword_value}
                            >
                              {kw.keyword_value}
                            </ShellLink>
                            {kw.managed_page_status && (
                              <span className={`inline-block mt-0.5 text-xs px-1.5 py-0.5 rounded-full ${getStatusColor(kw.managed_page_status)}`}>
                                {formatStatusLabel(kw.managed_page_status)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <LineChart width={80} height={24} data={kw.monthly_data}>
                            <Tooltip
                              offset={0}
                              position={{ y: -30 }}
                              content={({ active, payload }) => {
                                if (!active || !payload?.[0]) return null;
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow whitespace-nowrap">
                                    {d.month} {d.year}: {Number(d.searches).toLocaleString()}
                                  </div>
                                );
                              }}
                            />
                            <Line type="monotone" dataKey="searches" stroke={kw.growth_pct != null && kw.growth_pct > 0 ? '#16a34a' : '#dc2626'} dot={false} strokeWidth={1.5} isAnimationActive={false} />
                          </LineChart>
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <span className={kw.growth_pct != null && kw.growth_pct > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {kw.growth_pct != null ? `${kw.growth_pct > 0 ? '+' : ''}${kw.growth_pct.toLocaleString(undefined, { maximumFractionDigits: 0 })}%` : '-'}
                          </span>
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-300">
                          {kw.avg_monthly_searches != null ? kw.avg_monthly_searches.toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Keyword Coverage */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Keyword Coverage</h2>
          <div className="text-center mb-4">
            <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">{coveragePct}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
            <div
              className="bg-blue-500 h-3 rounded-full transition-all"
              style={{ width: `${coveragePct}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            {keywordsWithPages.toLocaleString()} of {stats.keyword_count.toLocaleString()} keywords have managed pages
          </p>
          {stats.keywords_without_pages > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1">
              {stats.keywords_without_pages.toLocaleString()} keyword{stats.keywords_without_pages !== 1 ? 's' : ''} without pages
            </p>
          )}
        </div>
      </div>

      {/* Row 4: Recent Activity */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h2>
        {stats.recent_pages.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm">No recent pages</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {stats.recent_pages.map((page) => (
              <div key={page.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <ShellLink
                    to={withReturnTo(`/page-builder/${page.id}`, '/', 'Home')}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium truncate"
                  >
                    {page.title || page.keyword_value || `Page #${page.id}`}
                  </ShellLink>
                  {page.status && (
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${getStatusColor(page.status)}`}>
                      {formatStatusLabel(page.status)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-3">
                  {page.topic_name && <span className="truncate max-w-[120px]">{page.topic_name}</span>}
                  <span className="whitespace-nowrap">{timeAgo(page.updated_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
